import { z } from "zod";
import { idSchema, languageTagSchema, minorAmountSchema, slugSchema } from "./common.ts";
import { editorDocumentSchema, type EditorDocument } from "./editor.ts";

/** EU Regulation 1169/2011 Annex II allergens. Only staff may set these — never inferred by AI. */
export const ALLERGENS = [
  "gluten", "crustaceans", "eggs", "fish", "peanuts", "soybeans", "milk", "nuts",
  "celery", "mustard", "sesame", "sulphites", "lupin", "molluscs",
] as const;
export const allergenSchema = z.enum(ALLERGENS);
export type Allergen = z.infer<typeof allergenSchema>;

export const DIETARY_TAGS = ["vegetarian", "vegan", "gluten-free", "halal", "kosher", "spicy", "new", "signature"] as const;
export const dietaryTagSchema = z.enum(DIETARY_TAGS);
export type DietaryTag = z.infer<typeof dietaryTagSchema>;

export const menuStatusSchema = z.enum(["draft", "published", "archived"]);
export type MenuStatus = z.infer<typeof menuStatusSchema>;

export const createMenuSchema = z.object({
  restaurantId: idSchema,
  name: z.string().min(1).max(120),
  slug: slugSchema.optional(),
  defaultLanguage: languageTagSchema.optional(),
  themeId: z.string().max(64).optional(),
  /** "blank" | "theme" create a draft directly; imports go through /imports. */
  template: z.enum(["blank", "theme", "sample"]).default("theme"),
});
export type CreateMenuInput = z.infer<typeof createMenuSchema>;

export const updateMenuSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  slug: slugSchema.optional(),
  enabledLanguages: z.array(languageTagSchema).min(1).max(31).optional(),
  orderingEnabled: z.boolean().optional(),
});

export const saveDraftSchema = z.object({
  document: editorDocumentSchema,
  expectedRevision: z.number().int().min(0),
});

export const publishSchema = z.object({
  expectedRevision: z.number().int().min(0).optional(),
  note: z.string().max(200).optional(),
});

export const rollbackSchema = z.object({ versionId: idSchema });

export const createCategorySchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(1000).optional(),
  sortOrder: z.number().int().optional(),
});
export const updateCategorySchema = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(1000).nullable().optional(),
  sortOrder: z.number().int().optional(),
  isVisible: z.boolean().optional(),
});
export const reorderSchema = z.object({ ids: z.array(idSchema).max(500) });

export const variantInputSchema = z.object({
  id: idSchema.optional(),
  name: z.string().min(1).max(80),
  priceMinor: minorAmountSchema,
});

export const createItemSchema = z.object({
  name: z.string().min(1).max(160),
  description: z.string().max(2000).optional(),
  priceMinor: minorAmountSchema.nullable().optional(),
  imageAssetId: idSchema.nullable().optional(),
  allergens: z.array(allergenSchema).optional(),
  dietaryTags: z.array(dietaryTagSchema).optional(),
  isAvailable: z.boolean().optional(),
  variants: z.array(variantInputSchema).max(20).optional(),
  sortOrder: z.number().int().optional(),
});
export type CreateItemInput = z.infer<typeof createItemSchema>;

export const updateItemSchema = createItemSchema.partial().extend({
  categoryId: idSchema.optional(),
  isVisible: z.boolean().optional(),
  /** Staff confirmation that allergen information has been reviewed. */
  allergensReviewed: z.boolean().optional(),
  /** Clears the import "needs review" flag. */
  reviewed: z.boolean().optional(),
  taxRateBps: z.number().int().min(0).max(10_000).nullable().optional(),
});

export const modifierOptionInputSchema = z.object({
  id: idSchema.optional(),
  name: z.string().min(1).max(80),
  priceDeltaMinor: z.number().int().min(0).max(10_000_000),
  isAvailable: z.boolean().optional(),
});
export const modifierGroupInputSchema = z
  .object({
    name: z.string().min(1).max(80),
    minSelect: z.number().int().min(0).max(20).default(0),
    maxSelect: z.number().int().min(1).max(20).default(1),
    options: z.array(modifierOptionInputSchema).min(1).max(30),
  })
  .refine((g) => g.minSelect <= g.maxSelect, { message: "minSelect must be <= maxSelect" });
export type ModifierGroupInput = z.infer<typeof modifierGroupInputSchema>;

// ---------- DTOs ----------

export type TranslationStatus = "missing" | "draft" | "needs_review" | "approved" | "stale";

export type VariantDto = { id: string; name: string; priceMinor: number; sortOrder: number; translations: Record<string, string> };
export type ModifierOptionDto = { id: string; name: string; priceDeltaMinor: number; isAvailable: boolean; sortOrder: number; translations: Record<string, string> };
export type ModifierGroupDto = {
  id: string;
  name: string;
  minSelect: number;
  maxSelect: number;
  sortOrder: number;
  translations: Record<string, string>;
  options: ModifierOptionDto[];
};

export type ItemDto = {
  id: string;
  categoryId: string;
  name: string;
  description: string | null;
  priceMinor: number | null;
  imageAssetId: string | null;
  imageUrl: string | null;
  allergens: Allergen[];
  dietaryTags: DietaryTag[];
  allergensReviewed: boolean;
  needsReview: boolean;
  reviewNotes: string[];
  isAvailable: boolean;
  isVisible: boolean;
  sortOrder: number;
  revision: number;
  taxRateBps: number | null;
  variants: VariantDto[];
  modifierGroups: ModifierGroupDto[];
  translations: Record<string, FieldTranslations>;
};

/** Per-language translation of name/description with per-field status (stale when the source field changed). */
export type FieldTranslations = {
  name: string | null;
  description: string | null;
  nameStatus: TranslationStatus;
  descriptionStatus: TranslationStatus;
  /** Worst of the field statuses. */
  status: TranslationStatus;
};

export type CategoryDto = {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isVisible: boolean;
  revision: number;
  translations: Record<string, FieldTranslations>;
  items: ItemDto[];
};

export type MenuSummaryDto = {
  id: string;
  restaurantId: string;
  name: string;
  slug: string;
  status: MenuStatus;
  defaultLanguage: string;
  enabledLanguages: string[];
  orderingEnabled: boolean;
  themeId: string;
  publishedVersionId: string | null;
  publishedAt: string | null;
  draftRevision: number;
  hasUnpublishedChanges: boolean;
  publicUrl: string;
  createdAt: string;
  updatedAt: string;
};

export type DraftDto = {
  menuId: string;
  revision: number;
  document: EditorDocument;
  updatedAt: string;
  updatedBy: string | null;
};

export type MenuVersionDto = {
  id: string;
  revision: number;
  publishedAt: string;
  publishedBy: string | null;
  note: string | null;
  isCurrent: boolean;
};

// ---------- Published snapshot (what diners see) ----------

export type PublicItem = {
  id: string;
  name: Record<string, string>;
  description: Record<string, string>;
  priceMinor: number | null;
  imageUrl: string | null;
  allergens: Allergen[];
  dietaryTags: DietaryTag[];
  variants: Array<{ id: string; name: Record<string, string>; priceMinor: number }>;
  modifierGroups: Array<{
    id: string;
    name: Record<string, string>;
    minSelect: number;
    maxSelect: number;
    options: Array<{ id: string; name: Record<string, string>; priceDeltaMinor: number }>;
  }>;
  /** Live operational overlay; not part of the immutable snapshot. */
  isAvailable?: boolean;
};

export type PublicCategory = {
  id: string;
  name: Record<string, string>;
  description: Record<string, string>;
  items: PublicItem[];
};

export type PublicMenuSnapshot = {
  snapshotVersion: 1;
  menu: { id: string; name: string; slug: string; defaultLanguage: string; languages: string[] };
  restaurant: {
    id: string;
    name: string;
    logoUrl: string | null;
    address: string | null;
    phone: string | null;
    website: string | null;
    currency: string;
    timezone: string;
    taxInclusive: boolean;
  };
  document: EditorDocument;
  categories: PublicCategory[];
  /** Map of media asset id → public URL for images used by blocks. */
  assets: Record<string, string>;
  /** Language → set of translated content keys that are falling back to the default language. */
  fallbacks: Record<string, number>;
  publishedAt: string;
  versionId: string;
};

export type PublicMenuResponse = PublicMenuSnapshot & {
  ordering: { enabled: boolean; paused: boolean; table: { id: string; label: string } | null };
  analytics: { tier: "basic" | "detailed" | "advanced" };
  branding: { showPoweredBy: boolean };
};

